import { useEffect, useRef, useState } from 'react'
import { abrirJanelaDestacada } from './abrirJanela'

// ---------------------------------------------------------------------------
// Hook da janela destacada.
//
// Nasceu no modo 2D (`viewer/views2d`) e passou para aqui quando o BCF, a
// caixa de corte e outros painéis passaram a destacar-se também. O
// mecanismo (window.open + clonagem dos estilos + portal do React) e o
// que fica a cargo de quem usa estão descritos em `abrirJanela.ts`.
// Para um painel inteiro com a barra "Voltar ao painel", usar o
// componente `JanelaDestacada`, que está por cima deste hook.
// ---------------------------------------------------------------------------

export {
  copyStyleNodes,
  prepareDetachedDocument,
} from './abrirJanela'

/** Identidade da janela do modo 2D, o primeiro a destacar-se. É a que o
 *  hook usa quando não lhe dão outra. */
export const DETACHED_WINDOW_NAME = 'vn2r-views2d'
export const DETACHED_WINDOW_FEATURES =
  'width=1280,height=900,resizable=yes,scrollbars=yes'
export const DETACHED_WINDOW_TITLE = 'VN2R openBIM Hub · Vistas 2D'

export interface DetachedWindowHandlers {
  /** O browser recusou abrir a janela (bloqueador de popups). */
  onBlocked: () => void
  /** A janela abriu. */
  onOpen?: () => void
  /** O utilizador fechou a janela (X do browser). */
  onClose: () => void
}

export interface DetachedWindow {
  /** A janela externa, para teclado e caixas de diálogo. */
  win: Window | null
  /** Onde o portal do React deve escrever. */
  container: HTMLDivElement | null
}

/** Identidade da janela a abrir. Sem isto o hook usa a do modo 2D. Cada
 *  sítio que destaca precisa do SEU nome: dois nomes iguais reaproveitam
 *  a mesma janela do browser e um destacar deitava o outro abaixo. */
export interface DetachedWindowOptions {
  name?: string
  title?: string
  features?: string
}

const CLOSED: DetachedWindow = { win: null, container: null }

/**
 * Abre (e fecha) a janela externa em função de `enabled`.
 *
 * O `window.open` corre SÍNCRONO no corpo do efeito de propósito: os
 * efeitos de um evento discreto correm ainda dentro da tarefa do clique,
 * por isso a activação do utilizador continua válida e o browser não
 * trata isto como popup espontâneo. Só a escrita do estado é adiada um
 * microtarefa (a regra da casa não deixa chamar `setState` em corpo de
 * efeito, e aqui não custa nada).
 */
export function useDetachedWindow(
  enabled: boolean,
  handlers: DetachedWindowHandlers,
  options?: DetachedWindowOptions,
): DetachedWindow {
  const name = options?.name ?? DETACHED_WINDOW_NAME
  const title = options?.title ?? DETACHED_WINDOW_TITLE
  const features = options?.features ?? DETACHED_WINDOW_FEATURES
  const [state, setState] = useState<DetachedWindow>(CLOSED)

  // Os handlers vão para uma ref para o efeito depender só de `enabled`:
  // um `pushToast` ou um `setSurface` com identidade nova não pode
  // fechar e reabrir a janela do utilizador. Escrita em efeito (não no
  // render) e declarado ANTES do efeito que a lê, para correr primeiro.
  const handlersRef = useRef(handlers)
  useEffect(() => {
    handlersRef.current = handlers
  })

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const aberta = abrirJanelaDestacada({
      nome: name,
      titulo: title,
      features,
      aoFechar: () => handlersRef.current.onClose(),
    })
    if (!aberta) {
      handlersRef.current.onBlocked()
      return
    }

    let live = true
    void Promise.resolve().then(() => {
      if (live) setState({ win: aberta.win, container: aberta.container })
    })
    handlersRef.current.onOpen?.()

    return () => {
      live = false
      aberta.libertar()
    }
  }, [enabled, name, title, features])

  // Com o modo desligado devolve-se já o estado fechado, sem esperar
  // pela limpeza: quem chama nunca vê um contentor de uma janela morta.
  return enabled ? state : CLOSED
}
